import React, { useEffect, useMemo, useState } from 'react';
import { X, Clock, MapPin, CalendarDays, CheckCircle2 } from 'lucide-react';
import { contact } from '../mock/mock';
import { useContent } from '../context/ContentContext';

// Cafe hours: Mon-Sun 7am-3pm. Slots every 30 min, last collection 2:30pm.
function buildSlots() {
  const slots = [];
  for (let h = 7; h <= 14; h += 1) {
    for (const m of [0, 30]) {
      const hour12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? 'am' : 'pm';
      const label = `${hour12}:${m === 0 ? '00' : '30'}${ampm}`;
      slots.push(label);
    }
  }
  return slots; // 7:00am .. 2:30pm
}

// Earliest collection date: orders must be placed by the cut-off the day before.
function buildDates(cutoffHour = 16, days) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setDate(start.getDate() + (now.getHours() >= cutoffHour ? 2 : 1));
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const out = [];
  const cursor = new Date(start);
  let safety = 0;
  while (out.length < 7 && safety < 40) {
    const nm = dayNames[cursor.getDay()];
    if (!days || !days.length || days.includes(nm)) out.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
    safety += 1;
  }
  return out;
}

const fmtDate = (d) =>
  d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

export default function PickupConfirmModal({ open, onClose, lines, total, count, onDone }) {
  const [render, setRender] = useState(open);
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState('');
  const [slot, setSlot] = useState('');
  const [done, setDone] = useState(false);

  const { content } = useContent();
  const pk = content.pickup || {};
  const dates = useMemo(() => buildDates(pk.cutoffHour ?? 16, pk.days), [open, pk.cutoffHour, pk.days]);
  const slots = useMemo(() => (pk.slots && pk.slots.length ? pk.slots : buildSlots()), [pk.slots]);

  useEffect(() => {
    if (open) {
      setRender(true);
      document.body.style.overflow = 'hidden';
      const raf = requestAnimationFrame(() => setShow(true));
      return () => cancelAnimationFrame(raf);
    }
    setShow(false);
    const t = setTimeout(() => {
      setRender(false);
      setDone(false);
      setName('');
      setPhone('');
      setDate('');
      setSlot('');
      document.body.style.overflow = '';
    }, 380);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!render) return null;

  const valid = name.trim() && date && slot;

  const handleConfirm = () => {
    if (!valid) return;
    setDone(true);
  };

  const handleClose = () => {
    if (done && onDone) onDone();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="pickup-confirm-modal"
      aria-modal="true"
      role="dialog"
    >
      <div
        onClick={handleClose}
        className={`absolute inset-0 bg-black/55 backdrop-blur-[3px] transition-opacity duration-300 ${
          show ? 'opacity-100' : 'opacity-0'
        }`}
      />

      <div
        className={`relative w-full sm:max-w-[460px] max-h-[92vh] overflow-y-auto bg-white text-[#1A1A1A] rounded-t-[28px] sm:rounded-[28px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)] ${
          show ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 sm:translate-y-4 scale-[0.98]'
        }`}
        style={{ transition: 'opacity 380ms cubic-bezier(0.16,1,0.3,1), transform 420ms cubic-bezier(0.16,1,0.3,1)' }}
      >
        <div className="sm:hidden pt-3 pb-1 flex justify-center">
          <span className="h-1.5 w-10 rounded-full bg-black/15" />
        </div>

        {!done ? (
          <div className="px-7 pt-6 sm:pt-8 pb-8">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#2C3A28]">
                  In store pick up
                </p>
                <h2 className="mt-2 font-display text-[26px] leading-none font-semibold tracking-tight text-[#2C3A28]">
                  Book your collection
                </h2>
              </div>
              <button
                onClick={onClose}
                data-testid="pickup-confirm-close"
                className="w-9 h-9 -mr-1 -mt-1 rounded-full flex items-center justify-center text-black/50 hover:text-black hover:bg-black/5 transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* 4pm note */}
            <div className="mt-5 rounded-2xl bg-[#EDEAE0] px-4 py-3.5 flex gap-3">
              <Clock size={18} className="text-[#8a6d3b] shrink-0 mt-0.5" />
              <p className="text-[13px] leading-relaxed text-[#5a4a32]">
                {pk.cutoffNote || 'Please place your order by 4:00pm the day before so we can have your freshly made nut milk ready for collection in store.'}
              </p>
            </div>

            {/* order summary */}
            <div className="mt-5 rounded-2xl border border-black/10 divide-y divide-black/5">
              {lines.map((l) => (
                <div key={l.key} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-black/70">
                    {l.qty} × {l.name} <span className="text-black/40">({l.badge} · {l.size})</span>
                  </span>
                  <span className="font-semibold">${l.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-black/60 text-sm">Total ({count} bottles)</span>
                <span className="font-display text-xl text-[#2C3A28]">${total.toFixed(2)}</span>
              </div>
            </div>

            {/* details */}
            <div className="mt-5 space-y-3">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="pickup-name-input"
                className="w-full rounded-xl border border-black/15 px-4 py-3 text-sm focus:outline-none focus:border-[#2C3A28]"
              />
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone (optional)"
                data-testid="pickup-phone-input"
                className="w-full rounded-xl border border-black/15 px-4 py-3 text-sm focus:outline-none focus:border-[#2C3A28]"
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <CalendarDays size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/40 pointer-events-none" />
                  <select
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="pickup-date-select"
                    className="w-full appearance-none rounded-xl border border-black/15 pl-9 pr-3 py-3 text-sm bg-white focus:outline-none focus:border-[#2C3A28]"
                  >
                    <option value="">Collection day</option>
                    {dates.map((d) => (
                      <option key={d.toISOString()} value={fmtDate(d)}>
                        {fmtDate(d)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <Clock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/40 pointer-events-none" />
                  <select
                    value={slot}
                    onChange={(e) => setSlot(e.target.value)}
                    data-testid="pickup-time-select"
                    className="w-full appearance-none rounded-xl border border-black/15 pl-9 pr-3 py-3 text-sm bg-white focus:outline-none focus:border-[#2C3A28]"
                  >
                    <option value="">Time</option>
                    {slots.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <button
              onClick={handleConfirm}
              disabled={!valid}
              data-testid="pickup-confirm-submit"
              className="mt-6 w-full rounded-full bg-[#2C3A28] text-[#E9EBED] py-4 font-semibold hover:bg-[#1f2a1c] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Confirm pick-up order
            </button>
          </div>
        ) : (
          <div className="px-7 pt-8 pb-9 text-center" data-testid="pickup-confirm-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#EDEAE0] flex items-center justify-center">
              <CheckCircle2 size={34} className="text-[#2C3A28]" />
            </div>
            <h2 className="mt-5 font-display text-2xl font-semibold text-[#2C3A28]">
              Thanks, {name.split(' ')[0]}!
            </h2>
            <p className="mt-3 text-sm text-black/60 leading-relaxed">
              Your pick-up box is booked for
              <br />
              <strong className="text-[#2C3A28]">{date} · {slot}</strong>
            </p>
            <div className="mt-5 rounded-2xl bg-[#EDEAE0] px-4 py-3.5 flex items-center justify-center gap-2 text-[13px] text-[#5a4a32]">
              <MapPin size={16} className="text-[#8a6d3b]" /> {contact.address}
            </div>
            <p className="mt-4 text-[12px] text-black/45 leading-relaxed">
              We'll have your freshly made nut milk ready to collect. Payment is made in store on collection.
            </p>
            <button
              onClick={handleClose}
              data-testid="pickup-success-done"
              className="mt-6 w-full rounded-full bg-[#2C3A28] text-white py-4 font-semibold hover:bg-[#1f2a1c] transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
